/**
 * Host-style URL without a scheme (e.g. Formstack embeds pasted without https://).
 * @param {string} trimmed
 * @returns {boolean}
 */
const looksLikeSchemelessHttpUrl = trimmed => {
  if (trimmed.startsWith('/')) return false
  if (/^localhost(?:\/|\?|#|:|$)/i.test(trimmed) || /^localhost$/i.test(trimmed)) {
    return true
  }
  const withPathOrQuery = /^[a-zA-Z0-9](?:[a-zA-Z0-9-]*\.)+[a-zA-Z]{2,}(?:\/|\?|#|$)/
  const bareHost = /^[a-zA-Z0-9](?:[a-zA-Z0-9-]*\.)+[a-zA-Z]{2,}$/
  return withPathOrQuery.test(trimmed) || bareHost.test(trimmed)
}

/**
 * Validates the universal-template form module embed URL.
 * Accepts absolute http(s) URLs, protocol-relative // URLs (resolved as https),
 * and scheme-less host URLs (e.g. subdomain.example.com/path) treated as https.
 * @param {string|undefined|null} url
 * @returns {string} Empty string if valid, otherwise an error message.
 */
export const getFormUrlValidationMessage = url => {
  const trimmed = String(url ?? '').trim()
  if (!trimmed) {
    return 'Form URL is required'
  }

  const candidates = []
  try {
    candidates.push(new URL(trimmed))
  } catch {
    // not an absolute URL as given
  }
  if (/^\/\//.test(trimmed)) {
    try {
      candidates.push(new URL(trimmed, 'https://placeholder.invalid'))
    } catch {
      // ignore
    }
  }
  const hasOwnScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed)
  if (!hasOwnScheme && looksLikeSchemelessHttpUrl(trimmed)) {
    try {
      candidates.push(new URL(`https://${trimmed}`))
    } catch {
      // ignore
    }
  }

  for (const u of candidates) {
    if (u.protocol === 'http:' || u.protocol === 'https:') {
      return ''
    }
  }

  try {
    const u = new URL(trimmed)
    if (u.protocol !== 'http:' && u.protocol !== 'https:') {
      return 'Form URL must use http:// or https://'
    }
  } catch {
    // fall through to generic message
  }

  return 'Form URL must be a valid URL (e.g. https://...)'
}
